import { Link, NavLink, Outlet, useLocation } from 'react-router'
import type { ReactNode } from 'react'
import { IconCatalog, IconHome, IconSearch, IconSettings } from './icons'
import { Logo, useLogoVariant } from './Logo'
import { anzahlUpdates } from '@/features/updates/diff'
import { useApp } from '@/store/app'

/**
 * Rahmen der App: Inhalt plus schwebende Navigationsleiste am unteren Rand.
 *
 * Unten, weil die App einhändig bedient wird — der Daumen erreicht den oberen
 * Bildschirmrand nicht. Schwebend statt angeschnitten, damit erkennbar bleibt, dass der
 * Inhalt darunter weitergeht.
 *
 * Drei Ziele in der Kapsel und, deutlich abgesetzt, die Suche als eigener Knopf. Die
 * Trennung ist keine Gestaltungslaune: Start, Katalog und Mehr sind **Orte** der App,
 * zwischen denen man wechselt — die Suche ist eine **Handlung**, die von jedem dieser
 * Orte ausgeht und dorthin zurückführt. Als vierter gleichrangiger Reiter hat sie dem
 * jahrelang widersprochen.
 */

// In der Leiste ist nur Platz für ein kurzes Wort, und sichtbar wird es nur am aktiven
// Eintrag. `description` nennt das Ziel ausführlich — vorgelesen wird die vollständige
// Fassung. Die Kurzform steht dabei am Anfang der Langform, damit gesprochener und
// sichtbarer Name zusammenpassen (WCAG 2.5.3).
const NAV = [
  { to: '/', label: 'Start', description: 'Start — Favoriten und Verlauf', Icon: IconHome, end: true },
  { to: '/katalog', label: 'Katalog', description: 'Katalog — alle Karten nach Kategorien', Icon: IconCatalog, end: false },
  {
    to: '/einstellungen',
    label: 'Mehr',
    description: 'Mehr — Einstellungen und Offline-Verwaltung',
    Icon: IconSettings,
    end: false,
  },
] as const

export function AppShell() {
  const { pathname } = useLocation()
  // In der Kartenansicht nicht: Dort zählt jeder Pixel für die Karte selbst, und sie
  // bringt mit Zurück-Knopf und Titel eine eigene Kopfzeile mit.
  const mitAbsender = !pathname.startsWith('/karte/')

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col">
      {mitAbsender && <Absender />}
      {/* Der untere Abstand entspricht exakt der Navigationsleiste — siehe
          --nav-hoehe in index.css. Der Inhalt scrollt sichtbar unter der Glasfläche
          durch, endet aber darüber. */}
      <main
        className="flex-1"
        style={{ paddingBottom: 'var(--nav-hoehe)' }}
      >
        <Outlet />
      </main>
      <BottomNav />
    </div>
  )
}

/**
 * Absenderleiste: Wer die App im Einsatz in die Hand bekommt, soll auf jedem
 * Bildschirm sehen, wessen Unterlagen er vor sich hat.
 */
function Absender() {
  const variant = useLogoVariant()
  // Abstand additiv: Die Utility-Klasse pt-safe und eine Tailwind-Klasse wie pt-2
  // setzen beide padding-top — eine gewinnt, die andere fällt weg. Auf dem Gerät
  // blieb so gar keine Luft über dem Logo.
  return (
    <header
      className="border-b border-line bg-bg-deep px-4"
      style={{
        // Oben mehr als unten: Unter der Leiste folgt noch der Abstand des
        // Ansichtskopfes, bevor die Überschrift beginnt. Gemessen waren es dort 38 px
        // gegenüber 25 px oben — mit diesem Wert liegt das Logo mittig zwischen
        // oberem Rand und Überschrift.
        paddingTop: 'calc(env(safe-area-inset-top) + 22px)',
        paddingBottom: '10px',
      }}
    >
      <div className="-ml-2">
        <Logo variant={variant} height={30} />
      </div>
    </header>
  )
}

function BottomNav() {
  // Neue oder geänderte Karten erreicht man über „Mehr" — dort steht die
  // Offline-Verwaltung. Ein Punkt am Symbol genügt als Hinweis; eine Zahl in einer
  // Leiste, die im Einsatz mit dem Daumen bedient wird, läse ohnehin niemand.
  const offeneUpdates = anzahlUpdates(useApp((s) => s.updates))
  const location = useLocation()

  return (
    // Der äußere Rahmen fängt keine Berührungen ab: Zwischen Kapsel und Suchknopf und
    // rechts wie links daneben liegt Inhalt, der bedienbar bleiben muss.
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center"
      style={{
        paddingBottom: 'calc(var(--nav-luft) + env(safe-area-inset-bottom))',
        paddingLeft: 'calc(var(--nav-luft) + env(safe-area-inset-left))',
        paddingRight: 'calc(var(--nav-luft) + env(safe-area-inset-right))',
      }}
    >
      {/* Weicher Auslauf hinter der Leiste: Der Inhalt scrollt sichtbar darunter durch,
          soll aber nicht mitten im Wort am Bildschirmrand abreißen. */}
      <div
        aria-hidden
        className="nav-verlauf pointer-events-none absolute inset-x-0 bottom-0 -z-10"
        style={{ height: 'calc(var(--nav-hoehe) + 1.5rem)' }}
      />
      <div className="pointer-events-auto flex w-full max-w-2xl items-center gap-2">
        <nav
          aria-label="Hauptnavigation"
          className="glas flex-1 rounded-full"
          style={{ height: 'var(--nav-kapsel)' }}
        >
          <ul className="flex h-full">
            {NAV.map(({ to, label, description, Icon, end }) => (
              <li key={to} className="flex-1">
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    'relative flex h-full items-center justify-center gap-1.5 rounded-full ' +
                    'text-[11px] font-semibold uppercase tracking-wider ' +
                    'transition-colors duration-100 ' +
                    // Menüelemente tragen laut Styleguide Dunkelblau; der aktive Punkt
                    // DRK-Rot als Aktionsfarbe. Als gefüllte Pille, weil die Leiste
                    // keine Kanten mehr hat, an denen eine Markierung sitzen könnte.
                    (isActive
                      ? 'auf-akzent bg-accent text-accent-ink'
                      : 'text-secondary hover:text-accent')
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className="relative">
                        <Icon className="size-6" />
                        {to === '/einstellungen' && offeneUpdates > 0 && !isActive && (
                          <span
                            aria-hidden
                            className="absolute -right-1 top-0 size-2 rounded-full bg-accent
                                       ring-2 ring-bg-deep"
                          />
                        )}
                      </span>
                      {/* Beschriftung nur am aktiven Eintrag: Auf der Glasfläche ist die
                          Subpixel-Glättung abgeschaltet, und drei dauerhaft weicher
                          gezeichnete Kleinwörter wären ein schlechter Tausch. Eines,
                          das sagt, wo man gerade ist, ist der Weichzeichnung wert. */}
                      {isActive && <span aria-hidden>{label}</span>}
                      <span className="sr-only">
                        {description}
                        {to === '/einstellungen' && offeneUpdates > 0
                          ? ` — ${offeneUpdates} Karten geändert`
                          : ''}
                      </span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bewusst ein Link und keine Schaltfläche: Die Suche behält ihre eigene Adresse
            mitsamt Suchbegriff. Der Zustand `hintergrund` sagt der App, dass das
            Suchblatt über die aktuelle Ansicht gelegt werden soll, statt sie zu
            ersetzen — siehe App.tsx. */}
        <Link
          to="/suche"
          state={{ hintergrund: location }}
          aria-label="Suche"
          className="glas flex shrink-0 items-center justify-center rounded-full
                     text-secondary transition-colors duration-100 hover:text-accent"
          style={{ height: 'var(--nav-kapsel)', width: 'var(--nav-kapsel)' }}
        >
          <IconSearch className="size-6" />
        </Link>
      </div>
    </div>
  )
}

/** Kopfbereich einer Ansicht. Titel im Display-Schnitt, darunter optional Werkzeuge. */
export function ViewHeader({
  title,
  hint,
  children,
}: {
  title: string
  hint?: ReactNode
  children?: ReactNode
}) {
  return (
    <header className="border-b border-line bg-bg-deep px-4 pb-3 pt-3">
      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-2xl font-bold leading-tight">{title}</h1>
        {hint && <span className="text-xs text-muted">{hint}</span>}
      </div>
      {children}
    </header>
  )
}
